import {
	Buildings,
	DownloadSimple,
	Eye,
	FileText,
	IdentificationBadge,
	Share,
	Warning,
} from '@phosphor-icons/react';
import {
	capitalizeFirstLetter,
	capitalizeText,
} from '../../../shared/utils/general.util';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import Modal from '../../../components/molecules/Modal/Modal';
import { useDispatch } from 'react-redux';
import useCleverTapEvent from '../../../hooks/useClevertapEvent';
import {
	ANALYTICS_EVENTS,
	EVENT_TYPES,
} from '../../../shared/constants/events';
import Alert, { ALERT_TYPE } from '../../../components/molecules/Alert';
import { hideAlert, showAlert } from '../../../redux/slices/alert.slice';
import LITERALS from '../../../shared/constants/literals';
import { ShareInfoZubale } from '../../../components/organisms/ShareInfoZubale/ShareInfoZubale';
import ROUTES from '../../../shared/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';

const FiscalProfileOverview = () => {
	const alert = useSelector((state: RootState) => state.alert);

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const logClevertapEvent = useCleverTapEvent();
	const userProfile = useSelector((state: RootState) => state.profile.user);
	const [showModal, setShowModal] = useState({
		negativeCompliance: false,
		share: false,
	});
	const [emailValue, setEmailValue] = useState('');
	const [showZubaleFlow, setShowZubaleFlow] = useState(false);
	const taxPayer = useSelector((state: RootState) => state.taxProfile.taxPayer);

	const messageShare = `¡Hola! A continuación te comparto mi información de facturación actualizada:${'\n'}
Nombre o razón social: ${
		taxPayer?.name
			?.split(' ')
			?.map((text) => capitalizeFirstLetter(text.toLocaleLowerCase()))
			?.join(' ') || ''
	}
RFC: ${taxPayer?.code || ''}
Código postal: ${taxPayer?.address?.postal_code || ''}${'\n'}
Mi(s) régimen(es) son: 
    ${
			taxPayer?.status?.tax_regimes
				?.map((r, index) => `${index + 1}. Régimen de ` + `${r.name} \n`)
				.join('') || ''
		}
Recuerda que puedes usar https://web.heru.app/ para realizar todas tus facturas gratis en cuestión de segundos${'\n'}
Saludos`;

	const logTaxProfileShareEvent = () => {
		if (userProfile) {
			logClevertapEvent(ANALYTICS_EVENTS.HOME_LINK, {
				event_type: EVENT_TYPES.HOME_TAX_PROFILE_SHARED,
				id: userProfile?.id,
				phone: userProfile?.cellphone,
			});
		}
	};

	const shareHandlerMobile = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();

		if (navigator.share) {
			logTaxProfileShareEvent();
			navigator
				.share({
					title: '',
					text: messageShare,
				})
				.then(() => {
					const alert = {
						type: ALERT_TYPE.SUCCESS,
						title: '¡Listo!',
						description: 'Tu información fiscal ha sido compartida',
					};
					dispatch(showAlert(alert));
				});
		} else {
			const alert = {
				type: ALERT_TYPE.WARNING,
				title: 'Aviso',
				description: LITERALS.ERROR_WEB_SHARE_MESSAGE,
			};
			dispatch(showAlert(alert));
		}
	};

	const fiscalSituationStatus = useMemo(() => {
		if (!taxPayer?.status?.status) return 'pending';
		if (
			taxPayer?.status?.status === 'ACTIVO' ||
			taxPayer?.status?.status === 'REACTIVADO'
		)
			return 'positive';
		return 'negative';
	}, [taxPayer]);

	const complianceOpinion = useMemo(() => {
		if (!taxPayer?.compliance?.result) return 'pending';
		if (taxPayer?.compliance.result === 'positive') return 'positive';
		return 'negative';
	}, [taxPayer]);

	const complianceOpinionLabel = useMemo(() => {
		if (taxPayer?.compliance?.result) {
			if (taxPayer?.compliance.result === 'negative') return 'Negativa';
			return 'Positiva';
		}
		return 'Sincronizando';
	}, [taxPayer]);

	const taxpayerInfoNotAvailable =
		!taxPayer?.status?.status ||
		!taxPayer?.compliance?.result ||
		!taxPayer?.compliance?.file?.file_url;

	const handleCloseShareModal = () => {
		setShowModal((prev) => ({ ...prev, share: false }));
		setShowZubaleFlow(false);
	};

	const isMobile = useMediaQuery('(max-width: 768px)');

	useEffect(() => {
		setEmailValue(messageShare);
	}, [messageShare]);

	useEffect(() => {
		if (alert.showAlert) {
			setTimeout(() => {
				dispatch(hideAlert());
			}, alert.duration);
		}
	}, [alert]);

	return (
		<div className='bg-white rounded-2xl shadow-sm p-3'>
			{alert.showAlert && (
				<Alert
					type={alert.type}
					description={alert.description}
					title={alert.title}
				/>
			)}
			<Modal
				open={showModal.share}
				onClose={handleCloseShareModal}
				sx='!p-2 w-[96%] max-w-lg max-h-[90dvh] overflow-y-auto'
			>
				{showZubaleFlow ? (
					<div>
						<ShareInfoZubale onClose={handleCloseShareModal} />
					</div>
				) : (
					<div className='p-2 md:p-3 flex flex-col gap-4'>
						<h3 className='text-xl font-semibold text-gray-900'>
							Compartir información
						</h3>
						<div className='whitespace-pre-line rounded-lg flex flex-col gap-4 text-h-dark'>
							<div className='flex flex-col gap-2'>
								<p className='text-sm opacity-50'>
									Está es la información que compartirás
								</p>
								<textarea
									className='w-full p-2 rounded-lg bg-transparent border border-h-neutral-80 outline-none'
									rows={14}
									value={emailValue}
									onChange={(e) => setEmailValue(e.target.value)}
								/>
							</div>
							{isMobile ? (
								<button
									onClick={shareHandlerMobile}
									className='bg-h-primary text-white px-4 py-2 rounded-lg h-12 font-medium'
								>
									Compartir
								</button>
							) : (
								<a
									href={`mailto:?subject=${encodeURIComponent('Información de facturación')}&body=${encodeURIComponent(emailValue)}`}
									className='bg-h-primary text-white rounded-lg h-12 font-medium text-center flex items-center justify-center'
								>
									Compartir por email
								</a>
							)}
						</div>
						<hr />
						<button
							onClick={() => setShowZubaleFlow(true)}
							className='font-medium text-h-primary-30 text-center w-fullrounded-lg underline'
						>
							Comparte tu información con alguna organización
						</button>
					</div>
				)}
			</Modal>
			<Modal
				open={showModal.negativeCompliance}
				onClose={() =>
					setShowModal((prev) => ({ ...prev, negativeCompliance: false }))
				}
				sx='!p-2'
			>
				<div className='p-6 max-w-md w-full bg-white rounded-lg'>
					<h3 className='text-xl font-semibold text-gray-900 mb-4'>
						Opinión de Cumplimiento Negativa
					</h3>

					<div className='mb-6'>
						<div className='flex items-center gap-3 mb-4 bg-h-warning-90 p-1 rounded-lg pl-2'>
							<Warning
								size={30}
								weight='duotone'
								className='text-amber-500'
							/>
							<p className='text-gray-700'>
								El SAT ha detectado que no has cumplido con ciertas obligaciones
								fiscales.
							</p>
						</div>

						<p className='text-gray-600 mb-3'>
							Una opinión de cumplimiento negativa puede afectar tu capacidad
							para:
						</p>

						<ul className='list-disc pl-5 mb-4 text-gray-600 space-y-1'>
							<li>Realizar ciertos trámites fiscales</li>
							<li>Mantener relaciones comerciales con algunas empresas</li>
							<li>Obtener ciertos créditos o beneficios fiscales</li>
						</ul>

						<p className='text-gray-600'>
							Es importante regularizar tu situación fiscal lo antes posible
							para evitar posibles sanciones o restricciones adicionales.
						</p>
					</div>

					<button
						onClick={() =>
							window.open(
								'https://heru.trb.ai/wa/lQi7a0',
								'_blank',
								'noopener=yes,noreferrer=yes',
							)
						}
						className='w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors'
					>
						<svg
							className='w-5 h-5'
							fill='currentColor'
							xmlns='http://www.w3.org/2000/svg'
							viewBox='0 0 24 24'
						>
							<path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z' />
						</svg>
						Hablar con un asesor
					</button>
				</div>
			</Modal>
			<div className='flex items-start justify-between mb-4'>
				<h2 className='text-lg font-bold text-gray-900'>Perfil Fiscal</h2>
				<button
					onClick={() => setShowModal((prev) => ({ ...prev, share: true }))}
					className='text-sm md:text-base bg-neutral-100 hover:bg-neutral-200 text-neutral-900 rounded-lg px-2 py-1 flex items-center gap-1 w-fit text-left whitespace-nowrap'
				>
					Compartir información
					<Share
						className='min-w-4 h-4'
						weight='bold'
					/>
				</button>
			</div>

			<div className='space-y-6'>
				<div className='space-y-4'>
					<div className='flex items-start gap-3'>
						<Buildings className='w-5 h-5 text-gray-500 mt-1 flex-shrink-0' />
						<div className='min-w-0'>
							<h3 className='font-medium text-gray-900 break-words'>
								{capitalizeText(taxPayer?.name || 'No disponible')}
							</h3>
							<p className='text-sm text-gray-500'>Razón Social</p>
						</div>
					</div>

					<div className='flex items-start gap-3'>
						<FileText className='w-5 h-5 text-gray-500 mt-1 flex-shrink-0' />
						<div className='min-w-0'>
							<h3 className='font-medium text-gray-900 break-words'>
								{taxPayer?.code || 'No disponible'}
							</h3>
							<p className='text-sm text-gray-500'>RFC</p>
						</div>
					</div>

					<div className='flex items-start gap-3'>
						<IdentificationBadge className='w-5 h-5 text-gray-500 mt-1 flex-shrink-0' />
						<div className='min-w-0'>
							<h3 className='font-medium text-gray-900'>Regímenes Fiscales</h3>
							{taxPayer?.status?.tax_regimes &&
							taxPayer?.status?.tax_regimes?.length > 0 ? (
								<div className='mt-2 flex flex-wrap gap-2'>
									{taxPayer?.status?.tax_regimes?.map((regime) => (
										<span
											key={regime.code}
											className='px-3 py-1 bg-neutral-50 text-gray-700 rounded-2xl text-sm'
										>
											{regime.name}
										</span>
									))}
								</div>
							) : (
								<p className='text-sm text-gray-500'>Sincronizando</p>
							)}
						</div>
					</div>
				</div>

				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					<div className='p-4 bg-neutral-50 rounded-xl flex flex-col h-full gap-3'>
						<div className='flex items-start justify-between gap-2'>
							<h3 className='font-medium text-gray-900'>
								Constancia de Situación Fiscal
							</h3>
							<span
								className={`px-3 py-1 rounded-full text-sm capitalize whitespace-nowrap ${
									fiscalSituationStatus === 'positive'
										? 'bg-green-100 text-green-800'
										: fiscalSituationStatus === 'negative'
											? 'bg-red-100 text-red-800'
											: 'bg-neutral-100 text-neutral-800'
								}`}
							>
								{capitalizeFirstLetter(
									taxPayer?.status?.status ?? 'Sincronizando',
								)}
							</span>
						</div>
						<p className='text-sm text-gray-600 flex-1'>
							{fiscalSituationStatus === 'positive'
								? 'Tu situación fiscal es positiva. Puedes recibir y emitir facturas sin ningún problema.'
								: fiscalSituationStatus === 'negative'
									? 'Tu RFC se encuentra inactivo, lo cual quiere decir que no puedes recibir o emitir facturas. Contáctanos para recibir ayuda.'
									: 'No tenemos información sobre tu situación fiscal. Contactános para recibir ayuda.'}
						</p>
						<button
							onClick={() =>
								window.open(taxPayer?.status?.file?.file_url ?? '')
							}
							disabled={!taxPayer?.status?.file?.file_url}
							className='w-full flex items-center justify-center gap-2 px-2 py-2 bg-white shadow-sm transition-all rounded-lg text-gray-700 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed'
						>
							<DownloadSimple className='w-4 h-4' />
							Descargar CSF
						</button>
					</div>

					<div className='p-4 bg-neutral-50 rounded-xl flex flex-col h-full gap-3'>
						<div className='flex items-center justify-between gap-2'>
							<h3 className='font-medium text-gray-900'>
								Opinión de Cumplimiento
							</h3>

							<button
								onClick={() =>
									complianceOpinion === 'negative' &&
									setShowModal({ ...showModal, negativeCompliance: true })
								}
								className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 cursor-pointer ${
									complianceOpinion === 'positive'
										? 'bg-green-100 text-green-800'
										: complianceOpinion === 'negative'
											? 'bg-red-100 text-red-800'
											: 'bg-neutral-100 text-neutral-800'
								}`}
							>
								{complianceOpinionLabel}
								{complianceOpinion === 'negative' && (
									<Eye
										size={16}
										weight='bold'
										className='ml-1'
									/>
								)}
							</button>
						</div>
						<p className='text-sm text-gray-600 mb-4 flex-1'>
							{complianceOpinion === 'positive'
								? 'En este momento tu opinión de cumplimiento es positiva. Asegúrate de ir al corriente con tu situación fiscal.'
								: complianceOpinion === 'negative'
									? 'El SAT ha detectado que no has cumplido con ciertas obligaciones fiscales. Regulariza tu situación lo antes posible.'
									: 'No tenemos información sobre tu opinión de cumplimiento. Contactános para recibir ayuda.'}
						</p>
						<button
							onClick={() =>
								window.open(taxPayer?.compliance?.file?.file_url ?? '')
							}
							disabled={!taxPayer?.compliance?.file?.file_url}
							className='w-full flex items-center justify-center gap-2 px-2 py-2 bg-white shadow-sm transition-all rounded-lg text-gray-700 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed'
						>
							<DownloadSimple className='w-4 h-4' />
							Descargar Opinión
						</button>
					</div>
				</div>

				{taxpayerInfoNotAvailable && (
					<div className='bg-neutral-100 text-neutral-800 p-2 rounded-lg'>
						<p className='font-medium'>
							Estamos terminando de sincronizar tu información del SAT
						</p>
						<p className='text-sm font-light leading-tight'>
							Este proceso puede tardar hasta 24 horas dependiendo del estado
							del SAT. Si después de este tiempo aún no tienes tu información,
							por favor{' '}
							<button
								className='underline'
								onClick={() => navigate(ROUTES.SUPPORT)}
							>
								contacta a soporte.
							</button>
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

export default FiscalProfileOverview;
